import React, { useState, useEffect } from 'react';
import { ShieldCheck, Hash, Server, Calculator, ArrowRight, Dna, CheckCircle2, Lock, Key } from 'lucide-react';
import { motion } from 'framer-motion';


import { useChatLayout } from '../context/ChatLayoutContext';
export const Fairness = () => {

  // 💬 Aç/kapa durumu paylaşımlı: sayfa değişse de korunur.
  const { chatOpen } = useChatLayout();
  const [serverSeed, setServerSeed] = useState('');
  const [publicSeed, setPublicSeed] = useState('');
  const [battleId, setBattleId] = useState('');
  const [roundIdx, setRoundIdx] = useState(0);
  const [slotIdx, setSlotIdx] = useState(0);
  const [totalWeight, setTotalWeight] = useState(1000);
  const [selectedGame, setSelectedGame] = useState('crash');
  const [verificationResult, setVerificationResult] = useState(null);

  // HMAC-SHA256 helper (matches Python's hmac.new)
  const hmacSha256Hex = async (key, msg) => {
    const enc = new TextEncoder();
    const cryptoKey = await crypto.subtle.importKey(
      'raw',
      enc.encode(key),
      { name: 'HMAC', hash: 'SHA-256' },
      false,
      ['sign']
    );
    const sig = await crypto.subtle.sign('HMAC', cryptoKey, enc.encode(msg));
    return Array.from(new Uint8Array(sig))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
  };

  const sha256Hex = async (msg) => {
    const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(msg));
    return Array.from(new Uint8Array(buf))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
  };

  const calculateResult = async () => {
    if (!serverSeed) {
      setVerificationResult(null);
      return;
    }

    try {
      let finalResultHtml = null;
      let hashDisplay = '';
      let hex8Display = '';
      let decimalDisplay = '';
      const commitHash = await sha256Hex(serverSeed);

      if (selectedGame === 'crash') {
        if (!publicSeed) {
          setVerificationResult({ commit: commitHash, needsPublic: true });
          return;
        }
        const fullHash = await hmacSha256Hex(serverSeed, publicSeed);
        const hex8 = fullHash.substring(0, 8);
        const decimal = parseInt(hex8, 16);
        let float_val = (decimal % 100000) / 100000.0;
        if (float_val === 1.0) float_val = 0.99999;
        let crash_point = Math.max(1.00, 0.93 / (1.0 - float_val));
        if (crash_point > 10000.0) crash_point = 10000.0;

        const color = crash_point < 2.0 ? 'text-rose-400' : crash_point < 10.0 ? 'text-[#2ecf6e]' : 'text-[#c98bf0]';
        hashDisplay = fullHash;
        hex8Display = hex8;
        decimalDisplay = decimal.toString();
        finalResultHtml = <span className={`text-4xl font-heading font-bold ${color}`}>{crash_point.toFixed(2)}x</span>;
      }
      else if (selectedGame === 'roulette') {
        if (!publicSeed) {
          setVerificationResult({ commit: commitHash, needsPublic: true });
          return;
        }
        const fullHash = await hmacSha256Hex(serverSeed, publicSeed);
        const hex8 = fullHash.substring(0, 8);
        const decimal = parseInt(hex8, 16);
        const winning_number = decimal % 15;
        const colorName = winning_number === 0 ? 'Green' : (winning_number <= 7 ? 'Red' : 'Black');
        const color = winning_number === 0 ? 'text-[#2ecf6e]' : (winning_number <= 7 ? 'text-rose-400' : 'text-zinc-400');

        hashDisplay = fullHash;
        hex8Display = hex8;
        decimalDisplay = decimal.toString();
        finalResultHtml = <span className={`text-4xl font-heading font-bold ${color}`}>{winning_number} <span className="text-zinc-500 text-2xl">({colorName})</span></span>;
      }
      else if (selectedGame === 'dice') {
        const fullHash = await sha256Hex(serverSeed);
        const hex8 = fullHash.substring(0, 8);
        const decimal = parseInt(hex8, 16);
        const roll_result = (decimal % 10001) / 100.0;

        hashDisplay = fullHash;
        hex8Display = hex8;
        decimalDisplay = decimal.toString();
        finalResultHtml = <span className="text-4xl font-heading font-bold text-[#c98bf0]">{roll_result.toFixed(2)}</span>;
      }
      else if (selectedGame === 'mines') {
        const tiles = [];
        for (let i = 0; i < 25; i++) {
          const h = await hmacSha256Hex(serverSeed, `mines:${i}`);
          tiles.push({ index: i, val: parseInt(h.substring(0, 8), 16) });
        }
        tiles.sort((a, b) => a.val - b.val);
        hashDisplay = 'Per-tile HMAC hashes computed (25 tiles)';
        hex8Display = '—';
        decimalDisplay = 'Sorted by hash value';

        finalResultHtml = (
          <div className="flex flex-col items-center text-center w-full">
            <span className="text-xs text-zinc-400 mb-3 uppercase tracking-widest">Bomb Priority Order (lowest hash = first bomb)</span>
            <div className="grid grid-cols-5 gap-1.5 w-full max-w-md">
              {tiles.map((t, i) => (
                <div key={t.index} className={`aspect-square rounded-lg flex flex-col items-center justify-center text-xs mm-num border ${i < 5 ? 'bg-rose-500/10 border-rose-500/40 text-rose-300' : 'bg-[#2A2D31]/30 border-[#2A2D31] text-zinc-400'}`}>
                  <span className="text-[10px] text-zinc-500">#{i + 1}</span>
                  <span className="font-bold">{t.index}</span>
                </div>
              ))}
            </div>
            <span className="text-[10px] text-zinc-500 mt-3">Top N tiles become bombs depending on mine count selected in the game.</span>
          </div>
        );
      }
      else if (selectedGame === 'tower') {
        const rows = [];
        for (let i = 0; i < 8; i++) {
          const h = await sha256Hex(`${serverSeed}:${i}`);
          rows.push({ row: i + 1, hex: h.substring(0, 8), dec: parseInt(h.substring(0, 8), 16) });
        }
        hashDisplay = 'Per-row SHA-256 hashes computed (8 rows)';
        hex8Display = '—';
        decimalDisplay = 'Each row uses its own derived hash';

        finalResultHtml = (
          <div className="flex flex-col items-center text-center w-full">
            <span className="text-xs text-zinc-400 mb-3 uppercase tracking-widest">Per-Row Hash Values</span>
            <div className="bg-[#15171a] w-full max-w-md p-3 rounded-lg flex flex-col gap-1 max-h-[160px] overflow-y-auto">
              {rows.map((r) => (
                <div key={r.row} className="flex justify-between items-center text-xs mm-num border-b border-[#2A2D31]/30 py-1 last:border-0">
                  <span className="text-zinc-500">Row {r.row}</span>
                  <span className="text-[#2ecf6e]">{r.hex}</span>
                  <span className="text-zinc-400">{r.dec}</span>
                </div>
              ))}
            </div>
            <span className="text-[10px] text-zinc-500 mt-2 max-w-md px-4">
              Bomb position within each row depends on difficulty (easy/normal/hard) you selected in the game.
            </span>
          </div>
        );
      }
      else if (selectedGame === 'cases') {
        const msg = `${battleId || 'battle_id'}:${roundIdx}:${slotIdx}`;
        const fullHash = await hmacSha256Hex(serverSeed, msg);
        const hex8 = fullHash.substring(0, 8);
        const decimal = parseInt(hex8, 16);
        const ticket = totalWeight > 0 ? decimal % totalWeight : 0;

        hashDisplay = fullHash;
        hex8Display = hex8;
        decimalDisplay = decimal.toString();

        finalResultHtml = (
          <div className="flex flex-col items-center text-center">
            <span className="text-xs text-zinc-400 mb-2 uppercase tracking-widest">Winning Ticket</span>
            <span className="text-4xl font-heading font-bold text-[#2ecf6e]">{ticket}</span>
            <span className="text-[10px] text-zinc-500 mt-3 max-w-sm px-4">
              The ticket number maps to an item by walking the case's weighted item list. You can see each case's item weights on its detail page.
            </span>
          </div>
        );
      }

      setVerificationResult({
        commit: commitHash,
        hash: hashDisplay,
        hex8: hex8Display,
        decimal: decimalDisplay,
        outcomeHtml: finalResultHtml
      });

    } catch (err) {
      console.error("Calculation error:", err);
    }
  };

  useEffect(() => {
    calculateResult();
    // eslint-disable-next-line
  }, [serverSeed, publicSeed, battleId, roundIdx, slotIdx, totalWeight, selectedGame]);

  const gameUsesPublicSeed = selectedGame === 'crash' || selectedGame === 'roulette';
  const gameIsCases = selectedGame === 'cases';

  return (
    <div
      /* 💬 Sohbet açıkken içerik sağa kayar (bkz. diğer sayfalar). */
      className={`min-h-screen bg-[#0E0F11] pt-24 pb-12 transition-[padding] duration-300 ${
        chatOpen ? 'md:pl-[340px] lg:pl-[370px]' : ''
      }`}
      data-testid="fairness-page"
    >
      <div className="max-w-[1200px] mx-auto px-4">

        {/* TITLE */}
        <div className="text-center mb-16">
          <motion.div
            initial={{ scale: 0 }} animate={{ scale: 1 }}
            className="w-20 h-20 bg-[#16A34A]/10 rounded-full flex items-center justify-center mx-auto mb-6 border border-[#16A34A]/20"
          >
            <ShieldCheck className="w-10 h-10 text-[#2ecf6e]" />
          </motion.div>
          <h1 className="text-4xl md:text-5xl font-heading font-bold text-white mb-4 uppercase tracking-wider">
            Provably <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#2ecf6e] to-[#16A34A]">Fair</span>
          </h1>
          <p className="text-zinc-400 max-w-2xl mx-auto text-lg">
            Every round is cryptographically sealed <span className="text-[#2ecf6e] font-semibold">before</span> it begins.
            You can independently reproduce any outcome and prove it was never tampered with.
          </p>
        </div>

        {/* 4-STEP FLOW */}
        <div className="flex flex-col md:flex-row gap-4 mb-16 items-center md:items-stretch">

          <div className="flex-1 bg-[#1A1D20] p-5 rounded-2xl border border-[#2A2D31] shadow-lg w-full flex flex-col items-center text-center">
            <Server className="w-8 h-8 text-[#c98bf0] mb-3" />
            <h3 className="text-base font-bold text-white mb-2">1. Server Seed</h3>
            <p className="text-zinc-400 text-xs leading-relaxed">
              Before each round, a fresh 64-char secret is generated with cryptographically secure randomness.
            </p>
          </div>

          <div className="flex items-center justify-center text-[#2A2D31]">
            <ArrowRight className="w-6 h-6 rotate-90 md:rotate-0" />
          </div>

          <div className="flex-1 bg-[#1A1D20] p-5 rounded-2xl border border-[#2A2D31] shadow-lg w-full flex flex-col items-center text-center">
            <Hash className="w-8 h-8 text-[#c98bf0] mb-3" />
            <h3 className="text-base font-bold text-white mb-2">2. Hash Commit</h3>
            <p className="text-zinc-400 text-xs leading-relaxed">
              Its SHA-256 hash is published publicly before betting opens. This locks the outcome.
            </p>
          </div>

          <div className="flex items-center justify-center text-[#2A2D31]">
            <ArrowRight className="w-6 h-6 rotate-90 md:rotate-0" />
          </div>

          <div className="flex-1 bg-[#1A1D20] p-5 rounded-2xl border border-[#2A2D31] shadow-lg w-full flex flex-col items-center text-center">
            <Key className="w-8 h-8 text-amber-400 mb-3" />
            <h3 className="text-base font-bold text-white mb-2">3. Public Seed</h3>
            <p className="text-zinc-400 text-xs leading-relaxed">
              A public value (announced before the round) mixes into the calculation, making prediction impossible.
            </p>
          </div>

          <div className="flex items-center justify-center text-[#2A2D31]">
            <ArrowRight className="w-6 h-6 rotate-90 md:rotate-0" />
          </div>

          <div className="flex-1 bg-[#1A1D20] p-5 rounded-2xl border border-[#2A2D31] shadow-lg w-full flex flex-col items-center text-center">
            <Dna className="w-8 h-8 text-[#2ecf6e] mb-3" />
            <h3 className="text-base font-bold text-white mb-2">4. Reveal</h3>
            <p className="text-zinc-400 text-xs leading-relaxed">
              After the round, the Server Seed is revealed. Anyone can reproduce the result below.
            </p>
          </div>

        </div>

        {/* WHY PAST ROUNDS DON'T PREDICT FUTURE ONES */}
        <div className="bg-[#1A1D20] rounded-2xl border border-[#16A34A]/20 p-6 shadow-lg mb-16">
          <h3 className="text-lg font-heading font-bold text-white mb-3 flex items-center gap-2">
            <Lock className="w-5 h-5 text-[#2ecf6e]" />
            Why past rounds tell you nothing about future ones
          </h3>
          <p className="text-zinc-400 text-sm leading-relaxed">
            Every round uses a <span className="text-white font-semibold">brand new</span> Server Seed generated from the operating system's cryptographic random source. SHA-256 is one-way, so even with millions of past hashes there is no statistical pattern, no sequence, and no leaked state. The seed of round N and the seed of round N+1 are mathematically unrelated — which is precisely why you can reproduce any result below <span className="text-white font-semibold">after</span> it's revealed, but never predict one <span className="text-white font-semibold">before</span>.
          </p>
        </div>

        {/* MATH EXPLANATIONS */}
        <div className="bg-[#1A1D20] rounded-2xl border border-[#2A2D31] p-8 shadow-2xl mb-16">
          <h2 className="text-2xl font-heading font-bold text-white mb-6 flex items-center gap-3">
            <Calculator className="w-6 h-6 text-[#B059E8]" />
            Game Mathematics
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="p-4 bg-[#15171a] rounded-xl border border-[#2A2D31]">
              <h4 className="text-rose-400 font-bold mb-2">Crash</h4>
              <p className="text-zinc-400 text-xs mb-2">
                HMAC of Server Seed + Public Seed. First 8 hex → decimal → modulo → float → multiplier formula.
              </p>
              <code className="block bg-[#1A1D20] p-3 rounded-lg text-[11px] text-[#c98bf0] overflow-x-auto leading-relaxed">
                hash = hmac_sha256(server_seed, public_seed)<br/>
                decimal = int(hash[:8], 16)<br/>
                f = (decimal % 100000) / 100000<br/>
                crash_point = 0.93 / (1 - f)
              </code>
            </div>

            <div className="p-4 bg-[#15171a] rounded-xl border border-[#2A2D31]">
              <h4 className="text-[#2ecf6e] font-bold mb-2">Roulette <span className="text-[10px] text-zinc-500 font-normal">(15 slots)</span></h4>
              <p className="text-zinc-400 text-xs mb-2">
                Same HMAC base as Crash, but mapped to a slot 0–14 (0 green, 1–7 red, 8–14 black).
              </p>
              <code className="block bg-[#1A1D20] p-3 rounded-lg text-[11px] text-[#5ee08f] overflow-x-auto leading-relaxed">
                hash = hmac_sha256(server_seed, public_seed)<br/>
                decimal = int(hash[:8], 16)<br/>
                winning_number = decimal % 15
              </code>
            </div>

            <div className="p-4 bg-[#15171a] rounded-xl border border-[#2A2D31]">
              <h4 className="text-[#c98bf0] font-bold mb-2">Dice</h4>
              <p className="text-zinc-400 text-xs mb-2">
                Each roll has its own one-shot Server Seed. Hash first 8 hex → decimal → 0.00–100.00 range.
              </p>
              <code className="block bg-[#1A1D20] p-3 rounded-lg text-[11px] text-[#c98bf0] overflow-x-auto leading-relaxed">
                hash = sha256(server_seed)<br/>
                decimal = int(hash[:8], 16)<br/>
                roll = (decimal % 10001) / 100
              </code>
            </div>

            <div className="p-4 bg-[#15171a] rounded-xl border border-[#16A34A]/30">
              <h4 className="text-[#2ecf6e] font-bold mb-2">Cases & Case Battles</h4>
              <p className="text-zinc-400 text-xs mb-2">
                HMAC is keyed with Server Seed and messaged with battle/round/slot identifiers. Result picks a weighted item via modulo.
              </p>
              <code className="block bg-[#1A1D20] p-3 rounded-lg text-[11px] text-[#5ee08f] overflow-x-auto leading-relaxed">
                msg = f"&#123;battle_id&#125;:&#123;round&#125;:&#123;slot&#125;"<br/>
                hash = hmac_sha256(server_seed, msg)<br/>
                ticket = int(hash[:8], 16) % total_weight
              </code>
            </div>

            <div className="p-4 bg-[#15171a] rounded-xl border border-[#2A2D31]">
              <h4 className="text-amber-400 font-bold mb-2">Mines <span className="text-[10px] text-zinc-500 font-normal">(25 tiles)</span></h4>
              <p className="text-zinc-400 text-xs mb-2">
                Each of the 25 tiles gets its own HMAC. Tiles are sorted by hash value — the lowest N become bombs.
              </p>
              <code className="block bg-[#1A1D20] p-3 rounded-lg text-[11px] text-amber-300 overflow-x-auto leading-relaxed">
                for i in 0..24:<br/>
                &nbsp;&nbsp;h[i] = hmac_sha256(server_seed, f"mines:&#123;i&#125;")<br/>
                sort tiles by int(h[:8], 16)<br/>
                first N tiles = bombs
              </code>
            </div>

            <div className="p-4 bg-[#15171a] rounded-xl border border-[#2A2D31]">
              <h4 className="text-amber-400 font-bold mb-2">Tower <span className="text-[10px] text-zinc-500 font-normal">(8 rows)</span></h4>
              <p className="text-zinc-400 text-xs mb-2">
                Each row derives its own hash from the Server Seed. Bomb column is picked by modulo, then the hash is shifted for the next bomb in the row.
              </p>
              <code className="block bg-[#1A1D20] p-3 rounded-lg text-[11px] text-amber-300 overflow-x-auto leading-relaxed">
                for row in 0..7:<br/>
                &nbsp;&nbsp;h = sha256(f"&#123;server_seed&#125;:&#123;row&#125;")<br/>
                &nbsp;&nbsp;bomb_idx = int(h[:8], 16) % cols<br/>
                &nbsp;&nbsp;h = h[8:]  # shift for next bomb
              </code>
            </div>
          </div>
        </div>

        {/* LIVE OUTCOME VERIFIER */}
        <div className="bg-gradient-to-br from-[#1A1D20] to-[#15171a] rounded-2xl border border-[#16A34A]/30 p-8 shadow-[0_0_30px_rgba(22,163,74,0.1)] relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-[#16A34A]/5 rounded-full blur-[80px] pointer-events-none" />

          <h2 className="text-2xl font-heading font-bold text-white mb-2 relative z-10 flex items-center gap-2">
            <CheckCircle2 className="w-6 h-6 text-[#2ecf6e]" />
            Live Outcome Verifier
          </h2>
          <p className="text-zinc-400 text-sm mb-6 relative z-10">
            Paste the values revealed at the end of a round. We'll reproduce the exact outcome in your browser using the same math the server uses.
          </p>

          <div className="space-y-5 relative z-10">

            {/* GAME SELECTOR */}
            <div className="flex flex-wrap gap-2">
              {['crash', 'roulette', 'dice', 'mines', 'tower', 'cases'].map((game) => (
                <button
                  key={game}
                  onClick={() => setSelectedGame(game)}
                  className={`px-5 py-2 rounded-xl font-bold text-sm uppercase tracking-wider transition-all border ${
                    selectedGame === game
                      ? 'bg-[#16A34A]/20 text-[#2ecf6e] border-[#16A34A]/50 shadow-[0_0_15px_rgba(22,163,74,0.2)]'
                      : 'bg-[#2A2D31]/30 text-zinc-400 border-[#2A2D31] hover:bg-[#2A2D31]/50'
                  }`}
                >
                  {game === 'cases' ? 'Cases / Battles' : game}
                </button>
              ))}
            </div>

            {/* SERVER SEED */}
            <div>
              <label className="text-xs font-bold text-zinc-500 uppercase tracking-wider mb-2 block">
                Revealed Server Seed
              </label>
              <input
                type="text"
                value={serverSeed}
                onChange={(e) => setServerSeed(e.target.value.trim())}
                placeholder="Paste the 64-character Server Seed revealed after the round..."
                className="w-full bg-[#15171a] border border-[#2A2D31] rounded-xl px-4 py-3 text-white mm-num text-sm focus:border-[#16A34A] outline-none transition-colors"
              />
            </div>

            {/* PUBLIC SEED (crash/roulette) */}
            {gameUsesPublicSeed && (
              <div>
                <label className="text-xs font-bold text-amber-500 uppercase tracking-wider mb-2 block">
                  Public Seed <span className="text-zinc-500 font-normal normal-case">(shown before the round began)</span>
                </label>
                <input
                  type="text"
                  value={publicSeed}
                  onChange={(e) => setPublicSeed(e.target.value.trim())}
                  placeholder="Paste the Public Seed announced before the round..."
                  className="w-full bg-[#15171a] border border-amber-500/40 rounded-xl px-4 py-3 text-white mm-num text-sm focus:border-amber-400 outline-none transition-colors"
                />
              </div>
            )}

            {/* CASES extra inputs */}
            {gameIsCases && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t border-[#2A2D31]/50 pt-4">
                <div>
                  <label className="text-xs font-bold text-[#16A34A] uppercase tracking-wider mb-2 block">Battle ID</label>
                  <input
                    type="text"
                    value={battleId}
                    onChange={(e) => setBattleId(e.target.value.trim())}
                    placeholder="e.g. battle_abc123"
                    className="w-full bg-[#15171a] border border-[#16A34A]/40 rounded-xl px-4 py-3 text-white mm-num text-sm focus:border-[#2ecf6e] outline-none transition-colors"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-[#16A34A] uppercase tracking-wider mb-2 block">Round Index</label>
                  <input
                    type="number"
                    min="0"
                    value={roundIdx}
                    onChange={(e) => setRoundIdx(parseInt(e.target.value) || 0)}
                    className="w-full bg-[#15171a] border border-[#16A34A]/40 rounded-xl px-4 py-3 text-white mm-num text-sm focus:border-[#2ecf6e] outline-none transition-colors"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-[#16A34A] uppercase tracking-wider mb-2 block">Slot Index</label>
                  <input
                    type="number"
                    min="0"
                    value={slotIdx}
                    onChange={(e) => setSlotIdx(parseInt(e.target.value) || 0)}
                    className="w-full bg-[#15171a] border border-[#16A34A]/40 rounded-xl px-4 py-3 text-white mm-num text-sm focus:border-[#2ecf6e] outline-none transition-colors"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-[#16A34A] uppercase tracking-wider mb-2 block">Total Weight <span className="text-zinc-500 font-normal normal-case">(from case details)</span></label>
                  <input
                    type="number"
                    min="1"
                    value={totalWeight}
                    onChange={(e) => setTotalWeight(parseInt(e.target.value) || 1)}
                    className="w-full bg-[#15171a] border border-[#16A34A]/40 rounded-xl px-4 py-3 text-white mm-num text-sm focus:border-[#2ecf6e] outline-none transition-colors"
                  />
                </div>
              </div>
            )}

            {/* RESULTS */}
            {verificationResult && verificationResult.needsPublic && (
              <div className="px-4 py-3 bg-amber-500/10 border border-amber-500/30 rounded-xl text-amber-300 text-sm">
                Paste the Public Seed above to reproduce this game's outcome.
              </div>
            )}

            {verificationResult && !verificationResult.needsPublic && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-[#2A2D31]/50">

                <div className="md:col-span-2">
                  <label className="text-[10px] font-bold text-zinc-600 uppercase tracking-wider mb-1 block">
                    SHA-256 Commit of Server Seed <span className="text-zinc-500 normal-case">(should match the pre-round hash shown in the game)</span>
                  </label>
                  <div className="w-full bg-[#15171a] border border-[#2A2D31] rounded-xl px-3 py-2 text-zinc-300 mm-num text-xs break-all">
                    {verificationResult.commit}
                  </div>
                </div>

                <div>
                  <label className="text-[10px] font-bold text-zinc-600 uppercase tracking-wider mb-1 block">Full Hash Output</label>
                  <div className="w-full bg-[#15171a] border border-[#2A2D31] rounded-xl px-3 py-2 text-zinc-300 mm-num text-xs break-all min-h-[40px]">
                    {verificationResult.hash}
                  </div>
                </div>

                <div>
                  <label className="text-[10px] font-bold text-zinc-600 uppercase tracking-wider mb-1 block">First 8 Hex → Decimal</label>
                  <div className="w-full bg-[#15171a] border border-[#2A2D31] rounded-xl px-3 py-2 text-zinc-300 mm-num text-xs min-h-[40px]">
                    <span className="text-[#c98bf0]">{verificationResult.hex8}</span>
                    {verificationResult.decimal && verificationResult.decimal !== '—' && !verificationResult.decimal.includes('Sorted') && !verificationResult.decimal.includes('derived') && ' = '}
                    {verificationResult.decimal && !verificationResult.decimal.includes('Sorted') && !verificationResult.decimal.includes('derived') ? verificationResult.decimal : ''}
                  </div>
                </div>

                <div className="md:col-span-2">
                  <label className="text-[10px] font-bold text-zinc-600 uppercase tracking-wider mb-1 block">Reproduced Outcome</label>
                  <div className="w-full bg-[#15171a] border border-[#16A34A]/30 rounded-xl px-4 py-5 flex items-center justify-center shadow-inner min-h-[120px]">
                    {verificationResult.outcomeHtml}
                  </div>
                </div>

              </div>
            )}

          </div>
        </div>

      </div>
    </div>
  );
};